import React, { useState } from "react";
import { ChevronUp, ChevronDown, X, Briefcase } from "lucide-react";
import { Button, inputClass } from "./kit";

// The roles Autopilot searches for — ORDERED, and capped, for a reason worth knowing.
//
// Each role is a SEPARATE query against the job feed every time the pool is rebuilt, and the feed
// bills per job returned. So the cap is a cost ceiling rather than a UI preference: three roles is
// three searches a day, four is four. The order matters too — the pool builder interleaves results
// round-robin in this order, so the first role's jobs naturally lead the grid.
//
// Deliberately the same shape as LocationPicker: numbered rows, ↑↓, ×. Candidates learn one control
// for "what" and "where" instead of two.
export const ROLE_MAX = 4;

export default function RolePicker({ value, onChange, label = "What roles are you looking for?" }) {
  const [draft, setDraft] = useState("");
  const list = (value || []).filter(Boolean);
  const full = list.length >= ROLE_MAX;

  const add = () => {
    const v = draft.trim();
    if (!v || full) return;
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...list, v]);
    setDraft("");
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
        {label}{" "}
        <span className="font-normal text-muted-foreground">
          — in priority order, up to {ROLE_MAX}
        </span>
      </label>

      {list.length > 0 && (
        <ul className="mb-2 rounded-xl border border-border divide-y divide-border overflow-hidden">
          {list.map((v, i) => (
            <li key={v} className="flex items-center gap-2 px-2.5 py-2 bg-white">
              <span className="w-5 h-5 shrink-0 rounded-md bg-[#0025D8]/[0.08] text-[#0025D8] text-[11px] font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <Briefcase className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 min-w-0 truncate text-[13px] text-[#0F172A]">{v}</span>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                aria-label={`Move ${v} up`}
                className="p-1 rounded-md text-muted-foreground hover:bg-secondary hover:text-[#0F172A] disabled:opacity-25 disabled:hover:bg-transparent">
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1}
                aria-label={`Move ${v} down`}
                className="p-1 rounded-md text-muted-foreground hover:bg-secondary hover:text-[#0F172A] disabled:opacity-25 disabled:hover:bg-transparent">
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(i)} aria-label={`Remove ${v}`}
                className="p-1 rounded-md text-muted-foreground hover:bg-[#E11D48]/10 hover:text-[#E11D48]">
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!full && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <input className={inputClass} value={draft} placeholder="e.g. Project Manager"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <Button type="button" variant="secondary" size="sm" disabled={!draft.trim()}
            onClick={add}>Add</Button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
        Use the title an employer would post, not your internal job grade.
      </p>
    </div>
  );
}
